import React, { useState } from 'react';
import { FUEL_RANGE_CURVE } from '../lib/constants';

const MAIN_CAP_GAL = 90;   // Fuel Tank 1 — Main
const AUX_CAP_GAL  = 38;   // Fuel Tank 2 — Aux
const L_PER_GAL    = 3.785;

function useIsMobile() {
  const [m, setM] = React.useState(typeof window !== 'undefined' && window.innerWidth < 760);
  React.useEffect(() => {
    const h = () => setM(window.innerWidth < 760);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// Quarter-step tank level indicator (E · ¼ · ½ · ¾ · F) — independent of gallons added.
const LEVELS = [[0,'E'],[0.25,'¼'],[0.5,'½'],[0.75,'¾'],[1,'F']];
function LevelPicker({ label, cap, unit, value, onChange }) {
  const gal = value != null && value !== '' ? Math.round(Number(value) * cap) : null;
  return (
    <div style={{ background:'var(--s2)', borderRadius:10, padding:'12px 14px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--gold)' }}>{label}</span>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)' }}>{gal != null ? `≈ ${gal} ${unit}` : `${cap} ${unit} cap`}</span>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        {LEVELS.map(([v,l]) => (
          <button key={v} onClick={()=>onChange(Number(value)===v?'':v)} style={{ flex:1, padding:'11px 0', borderRadius:7, border: Number(value)===v?'none':'1px solid var(--border)', background: Number(value)===v?'var(--navy)':'var(--surface)', color: Number(value)===v?'var(--gold-lt)':'var(--slate)', fontFamily:"'IBM Plex Mono',monospace", fontSize:14, fontWeight:700, cursor:'pointer' }}>{l}</button>
        ))}
      </div>
    </div>
  );
}

// ── Schedule ─────────────────────────────────────────────────
const DEFAULT_SCHEDULE = [
  { id:'eng_oil',   name:'Engine Oil & Filter',          system:'Engine',    intervalHrs:100,  lastHrs:3200,  notes:'' },
  { id:'eng_racor', name:'Fuel Pre-filter (Racor)',       system:'Engine',    intervalHrs:100,  lastHrs:3200,  notes:'' },
  { id:'eng_imp',   name:'Raw Water Impeller',            system:'Engine',    intervalHrs:200,  lastHrs:3080,  notes:'' },
  { id:'eng_belt',  name:'Drive Belt Check',              system:'Engine',    intervalHrs:200,  lastHrs:3080,  notes:'' },
  { id:'eng_fuel',  name:'Engine Fuel Filter',            system:'Engine',    intervalHrs:200,  lastHrs:3150,  notes:'' },
  { id:'eng_valve', name:'Valve Clearance Check',         system:'Engine',    intervalHrs:300,  lastHrs:3000,  notes:'' },
  { id:'eng_cool',  name:'Coolant Flush & Replace',       system:'Engine',    intervalHrs:500,  lastHrs:3000,  notes:'' },
  { id:'gen_oil',   name:'Generator Oil & Filter',        system:'Generator', intervalHrs:100,  lastHrs:1090,  notes:'' },
  { id:'gen_fuel',  name:'Generator Fuel Filter',         system:'Generator', intervalHrs:200,  lastHrs:1000,  notes:'' },
  { id:'gen_imp',   name:'Generator Raw Water Impeller',  system:'Generator', intervalHrs:200,  lastHrs:1000,  notes:'' },
  { id:'gen_belt',  name:'Generator Drive Belt',          system:'Generator', intervalHrs:300,  lastHrs:1000,  notes:'' },
  { id:'htr_filter',name:'Heater Filter Cleaned',         system:'Heater',    intervalHrs:200,  lastHrs:1000,  notes:'' },
  { id:'htr_annual',name:'Heater Annual Service',         system:'Heater',    intervalDays:365, lastDate:'2025-04-01', notes:'' },
  { id:'zincs',     name:'Zincs — Hull',                  system:'Seasonal',  intervalDays:180, lastDate:'2025-03-03', notes:'' },
  { id:'bottom',    name:'Bottom Paint',                  system:'Seasonal',  intervalDays:365, lastDate:'2025-03-03', notes:'' },
  { id:'rigging',   name:'Standing Rigging Inspect',      system:'Seasonal',  intervalDays:365, lastDate:'2025-04-01', notes:'' },
];

function pctColor(pct) {
  if (pct >= 1)   return 'var(--red)';
  if (pct >= 0.8) return '#D97706';
  return 'var(--green)';
}

// ── Shared primitives ─────────────────────────────────────────
function Lbl({ children, sub }) {
  return (
    <div style={{ marginBottom:4, minHeight:25 }}>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--slate)' }}>{children}</div>
      {sub && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', opacity:.7, marginTop:1 }}>{sub}</div>}
    </div>
  );
}

function GearIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  );
}

// ── System block inside unified modal ─────────────────────────
function SystemBlock({ title, icon, data, onChange, currentHrs, checks, noteLabel }) {
  const set = (k, v) => onChange(k, v);
  const nextOilDue = data.oil_hrs ? (Number(data.oil_hrs) + 20).toFixed(0) : null;

  return (
    <div style={{ background:'var(--s2)', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--navy)', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
        <span>{icon}</span> {title}
      </div>
      {checks.map(chk => (
        <div key={chk.key}>
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'6px 0', borderBottom:'1px solid rgba(0,0,0,.05)' }}>
            <input type="checkbox" checked={!!data[chk.key]} onChange={e=>set(chk.key, e.target.checked)} style={{ width:16, height:16, accentColor:'var(--navy)', cursor:'pointer', flexShrink:0 }}/>
            <span style={{ fontSize:13, color:'var(--ink-s)', flex:1 }}>{chk.label}</span>
          </label>
          {/* Oil details expand */}
          {chk.key === 'oil_checked' && data.oil_checked && (
            <div style={{ marginLeft:24, marginTop:6, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:6 }}>
              <div><Lbl>Qt added</Lbl><input type="number" step=".25" value={data.oil_qty||''} onChange={e=>set('oil_qty',e.target.value)} placeholder="0.5 qt" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
              <div><Lbl>Hrs at check</Lbl><input type="number" step=".1" value={data.oil_hrs||currentHrs||''} onChange={e=>set('oil_hrs',e.target.value)} placeholder={String(currentHrs||'')} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
              {nextOilDue && (
                <div style={{ gridColumn:'1/-1', background:'rgba(184,134,11,.1)', border:'1px solid rgba(184,134,11,.25)', borderRadius:6, padding:'6px 10px', display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--gold)', fontWeight:700 }}>Next oil due</span>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:700, color:'var(--navy)' }}>{nextOilDue}h</span>
                </div>
              )}
            </div>
          )}
          {/* Annual service expand */}
          {chk.key === 'annual_service' && data.annual_service && (
            <div style={{ marginLeft:24, marginTop:6, marginBottom:6 }}>
              <div><Lbl>Service date</Lbl><input type="date" value={data.service_date||''} onChange={e=>set('service_date',e.target.value)}/></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Unified check modal ───────────────────────────────────────
function CheckModal({ isOpen, onClose, onSave, currentEngHrs, currentGenHrs, editEntry }) {
  const [date, setDate]     = useState('');
  const [by, setBy]         = useState('Frederick');
  const [notes, setNotes]   = useState('');
  const [eng, setEng]       = useState({});
  const [gen, setGen]       = useState({});
  const [htr, setHtr]       = useState({});
  const [custom, setCustom] = useState({ name:'', hrs:'', cost:'', vendor:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const mobile = useIsMobile();

  React.useEffect(() => {
    if (!isOpen) return;
    if (editEntry) {
      setDate(editEntry.date || new Date().toISOString().slice(0,10));
      setBy(editEntry.performed_by || 'Frederick');
      setNotes(editEntry.notes || '');
      setEng(editEntry.engine || {});
      setGen(editEntry.genset || {});
      setHtr(editEntry.heater || {});
      setCustom({ name:'', hrs:'', cost:'', vendor:'', notes:'' });
    } else {
      setDate(new Date().toISOString().slice(0,10));
      setBy('Frederick'); setNotes('');
      setEng({}); setGen({}); setHtr({});
      setCustom({ name:'', hrs:'', cost:'', vendor:'', notes:'' });
    }
  }, [isOpen, editEntry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ id:editEntry?.id, date, performed_by:by, notes, engine:eng, genset:gen, heater:htr, custom });
      onClose();
    } catch {}
    finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const ENGINE_CHECKS = [
    { key:'oil_checked',      label:'Oil level checked' },
    { key:'belt_checked',     label:'Drive belt checked' },
    { key:'strainer_cleaned', label:'Raw water strainer cleaned' },
  ];
  const GEN_CHECKS = [
    { key:'oil_checked',      label:'Oil level checked' },
    { key:'belt_checked',     label:'Drive belt checked' },
    { key:'strainer_cleaned', label:'Raw water strainer cleaned' },
  ];
  const HTR_CHECKS = [
    { key:'filter_cleaned',   label:'Combustion air filter cleaned' },
    { key:'fuel_line',        label:'Fuel line & connections checked' },
    { key:'annual_service',   label:'Annual service / burner clean' },
  ];

  const hasCustom = custom.name.trim().length > 0;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:300, display:'flex', alignItems: mobile?'flex-end':'center', justifyContent:'center', padding: mobile?0:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'var(--surface)', borderRadius: mobile?'16px 16px 0 0':16, width:'100%', maxWidth:580, maxHeight: mobile?'90vh':'90vh', overflowY:'auto', boxShadow:'var(--sh-lg)' }}>

        <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'var(--surface)', zIndex:1 }}>
          <div style={{ fontSize:17, fontWeight:700, color:'var(--navy)' }}>Log Maintenance Check</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, color:'var(--slate)', cursor:'pointer' }}>✕</button>
        </div>

        <div style={{ padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10, marginBottom:14 }}>
            <div><Lbl>Date</Lbl><input type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
            <div><Lbl>Performed By</Lbl><input type="text" value={by} onChange={e=>setBy(e.target.value)}/></div>
          </div>

          <SystemBlock title="Engine — Yanmar 4JH3TE" icon="⚙" data={eng} onChange={(k,v)=>setEng(e=>({...e,[k]:v}))} currentHrs={currentEngHrs} checks={ENGINE_CHECKS}/>
          <SystemBlock title="Generator — Northern Lights" icon="⚡" data={gen} onChange={(k,v)=>setGen(g=>({...g,[k]:v}))} currentHrs={currentGenHrs} checks={GEN_CHECKS}/>
          <SystemBlock title="Heater — Webasto" icon="🔥" data={htr} onChange={(k,v)=>setHtr(h=>({...h,[k]:v}))} currentHrs={null} checks={HTR_CHECKS}/>

          {/* Custom item — ignored if name blank */}
          <div style={{ background:'var(--s2)', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--navy)', marginBottom:10 }}>
              ✏ Custom Item <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10, fontWeight:400, textTransform:'none', letterSpacing:0, color:'var(--slate)' }}>(optional — leave blank to skip)</span>
            </div>
            <div style={{ marginBottom:8 }}><Lbl>Item Name</Lbl><input type="text" value={custom.name} onChange={e=>setCustom(c=>({...c,name:e.target.value}))} placeholder="Fridge compressor, Watermaker, Winch service…"/></div>
            {hasCustom && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div><Lbl>Engine Hrs</Lbl><input type="number" step=".1" value={custom.hrs} onChange={e=>setCustom(c=>({...c,hrs:e.target.value}))} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
                <div><Lbl>Cost ($)</Lbl><input type="number" step=".01" value={custom.cost} onChange={e=>setCustom(c=>({...c,cost:e.target.value}))} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
                <div style={{ gridColumn:'1/-1' }}><Lbl>Vendor</Lbl><input type="text" value={custom.vendor} onChange={e=>setCustom(c=>({...c,vendor:e.target.value}))} placeholder="Fisheries Supply, West Marine…"/></div>
                <div style={{ gridColumn:'1/-1' }}><Lbl>Notes</Lbl><textarea value={custom.notes} onChange={e=>setCustom(c=>({...c,notes:e.target.value}))} placeholder="Parts used, observations…" style={{ minHeight:56 }}/></div>
              </div>
            )}
          </div>

          <div style={{ marginBottom:12 }}><Lbl>General Notes</Lbl><textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Anything worth flagging…" style={{ minHeight:60 }}/></div>
        </div>

        <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8, position:'sticky', bottom:0, background:'var(--surface)', paddingBottom:'max(12px, env(safe-area-inset-bottom))' }}>
          <button onClick={onClose} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, padding:'9px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--slate)', cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:700, padding:'9px 22px', borderRadius:7, border:'none', background:'linear-gradient(135deg,var(--navy),#1d3e63)', color:'var(--gold-lt)', opacity:saving?.7:1, cursor:'pointer' }}>
            {saving ? 'Saving…' : 'Save Check'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Check card ────────────────────────────────────────────────
function CheckPill({ done, label }) {
  return <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:600, marginRight:16, color: done?'var(--green)':'var(--slate)' }}>{done?'✓':'○'} {label}</span>;
}

function MSecLbl({ children }) {
  return <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--gold-deep, #8B6508)', margin:'18px 0 9px' }}>{children}</div>;
}
function checkSystems(entry) {
  const e = entry.engine||{}, g = entry.genset||{}, h = entry.heater||{};
  const s = [];
  if (e.oil_checked||e.belt_checked||e.strainer_cleaned) s.push('Engine');
  if (g.oil_checked||g.belt_checked||g.strainer_cleaned) s.push('Generator');
  if (h.filter_cleaned||h.fuel_line||h.annual_service)   s.push('Heater');
  return s;
}
function checkItems(entry) {
  const e = entry.engine||{}, g = entry.genset||{}, h = entry.heater||{};
  const it = [];
  if (e.oil_checked) it.push('Oil'); if (e.belt_checked) it.push('Belt'); if (e.strainer_cleaned) it.push('Strainer');
  if (g.oil_checked) it.push('Gen oil'); if (g.belt_checked) it.push('Gen belt'); if (g.strainer_cleaned) it.push('Gen strainer');
  if (h.filter_cleaned) it.push('Htr filter'); if (h.fuel_line) it.push('Htr fuel'); if (h.annual_service) it.push('Htr annual');
  return it;
}

// ── Maintenance check — summary card (media-left, matches logbook) ──
function CheckSummaryCard({ entry, active, onOpen }) {
  const eng = entry.engine||{};
  const systems = checkSystems(entry);
  const items = checkItems(entry);
  const title = systems.length ? `${systems.join(' · ')} service` : 'Maintenance check';
  return (
    <div onClick={()=>onOpen(entry)} style={{ background:'var(--surface)', borderRadius:12, display:'flex', overflow:'hidden', marginBottom:10, cursor:'pointer', transition:'box-shadow .15s', border: active?'1px solid var(--navy)':'1px solid var(--border)', boxShadow: active?'0 0 0 1px var(--navy), var(--sh)':'var(--sh)' }}>
      <div style={{ width:100, flexShrink:0, background:'var(--s2)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:6 }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--navy)' }}>{systems.length ? systems.map(s=>s[0]).join('·') : '🔧'}</div>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginTop:4, letterSpacing:'.08em', textTransform:'uppercase' }}>Check</div>
      </div>
      <div style={{ flex:1, minWidth:0, padding:'12px 15px' }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--gold-deep, #8B6508)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {entry.date}{entry.performed_by ? ` · ${entry.performed_by}` : ''}{eng.oil_hrs ? ` · ${Number(eng.oil_hrs).toLocaleString()} eng h` : ''}
        </div>
        <div style={{ fontWeight:700, color:'var(--navy)', fontSize:16, margin:'3px 0 4px', lineHeight:1.2 }}>{title}</div>
        {entry.notes && <div style={{ fontSize:12.5, color:'var(--slate)', lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{entry.notes}</div>}
        {items.length > 0 && (
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:'var(--navy)', fontWeight:600, marginTop:9 }}>
            {items.slice(0,4).join(' · ')}{items.length>4?` +${items.length-4}`:''}
            {eng.oil_checked && eng.oil_qty ? <span style={{ color:'var(--slate)', fontWeight:400 }}> &nbsp;·&nbsp; +{eng.oil_qty} qt</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Maintenance check — detail view (mobile full-screen / desktop pane) ──
function CheckDetailView({ entry, onEdit, onDelete, onBack }) {
  if (!entry) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'var(--slate)', fontSize:14 }}>Select a check</div>;
  const eng = entry.engine||{}, gen = entry.genset||{}, htr = entry.heater||{};
  const systems = checkSystems(entry);
  const title = systems.length ? `${systems.join(' · ')} service` : 'Maintenance check';
  const hasEng = eng.oil_checked||eng.belt_checked||eng.strainer_cleaned;
  const hasGen = gen.oil_checked||gen.belt_checked||gen.strainer_cleaned;
  const hasHtr = htr.filter_cleaned||htr.fuel_line||htr.annual_service;
  return (
    <div style={{ background:'var(--bg)' }}>
      <div style={{ position:'relative', height:120, background:'var(--s2)', borderBottom:'1px solid var(--border)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'var(--gold-deep, #8B6508)' }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'.2em', textTransform:'uppercase' }}>Maintenance Check</div>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:'var(--slate)', marginTop:6 }}>{entry.date}</div>
        {onBack && <div onClick={onBack} style={{ position:'absolute', top:12, left:12, width:34, height:34, borderRadius:'50%', background:'var(--surface)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'var(--sh)', fontSize:18, color:'var(--navy)' }}>‹</div>}
      </div>
      <div style={{ padding:'16px 18px 24px' }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--gold-deep, #8B6508)' }}>{entry.date}{entry.performed_by?` · By ${entry.performed_by}`:''}</div>
        <div style={{ fontSize:22, fontWeight:800, color:'var(--navy)', margin:'5px 0 3px' }}>{title}</div>
        {eng.oil_hrs && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:'var(--slate)' }}>Engine {Number(eng.oil_hrs).toLocaleString()} h at service</div>}

        {hasEng && (<><MSecLbl>Engine</MSecLbl>
          <div><CheckPill done={eng.oil_checked} label="Oil"/><CheckPill done={eng.belt_checked} label="Belt"/><CheckPill done={eng.strainer_cleaned} label="Strainer"/></div>
          {eng.oil_checked && eng.oil_qty && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:'var(--slate)', marginTop:6 }}>+{eng.oil_qty} qt oil added</div>}
        </>)}
        {hasGen && (<><MSecLbl>Generator</MSecLbl>
          <div><CheckPill done={gen.oil_checked} label="Oil"/><CheckPill done={gen.belt_checked} label="Belt"/><CheckPill done={gen.strainer_cleaned} label="Strainer"/></div>
        </>)}
        {hasHtr && (<><MSecLbl>Heater</MSecLbl>
          <div><CheckPill done={htr.filter_cleaned} label="Filter"/><CheckPill done={htr.fuel_line} label="Fuel line"/><CheckPill done={htr.annual_service} label="Annual"/></div>
        </>)}
        {entry.notes && (<><MSecLbl>Notes</MSecLbl><div style={{ fontSize:14, color:'var(--ink)', lineHeight:1.7 }}>{entry.notes}</div></>)}

        <div style={{ display:'flex', gap:8, marginTop:22, paddingTop:14, borderTop:'1px solid var(--border)' }}>
          <button onClick={()=>onEdit(entry)} style={{ flex:1, background:'none', border:'1px solid var(--border)', borderRadius:7, padding:'10px', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--slate)', cursor:'pointer' }}>Edit</button>
          <button onClick={()=>onDelete(entry)} style={{ flex:1, background:'none', border:'1px solid var(--border)', borderRadius:7, padding:'10px', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--red)', cursor:'pointer' }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Schedule row ──────────────────────────────────────────────
function ScheduleRow({ item, engHrs, genHrs, onUpdate }) {
  const [editing, setEditing] = React.useState(false);
  const [editHrs,  setEditHrs]    = React.useState('');
  const [editDate, setEditDate]   = React.useState('');
  const [editCost, setEditCost]   = React.useState('');
  const [editVendor,setEditVendor]= React.useState('');
  const [editNote, setEditNote]   = React.useState('');

  let pct=0, status='', dueAt=null;
  if (item.intervalHrs) {
    const current = item.system==='Generator' ? genHrs : engHrs;
    dueAt = item.lastHrs + item.intervalHrs;
    const used = current - item.lastHrs;
    pct = Math.min(Math.max(used/item.intervalHrs, 0), 1);
    const rem = dueAt - current;
    if (rem<=0) status = `OVERDUE — ${Math.abs(Math.round(rem))}h past due`;
    else if (pct>=0.8) status = `Due in ${Math.round(rem)}h`;
    else status = `${Math.round(rem)}h remaining`;
  } else if (item.intervalDays) {
    const daysUsed = (new Date()-new Date(item.lastDate))/86400000;
    pct = Math.min(Math.max(daysUsed/item.intervalDays,0),1);
    const rem = item.intervalDays - daysUsed;
    if (rem<=0) status='OVERDUE'; else status=`${Math.round(rem)} days remaining`;
  }
  const color = pctColor(pct);

  const openEdit = () => {
    setEditHrs(item.lastHrs?String(item.lastHrs):'');
    setEditDate(item.lastDate||new Date().toISOString().slice(0,10));
    setEditCost(''); setEditVendor(''); setEditNote(item.notes||'');
    setEditing(true);
  };
  const saveEdit = () => {
    const updates = { notes:editNote, cost:editCost||null, vendor:editVendor||null };
    if (item.intervalHrs && editHrs) updates.lastHrs = Number(editHrs);
    if (editDate) updates.lastDate = editDate;
    onUpdate(item.id, updates);
    setEditing(false);
  };

  return (
    <div style={{ background:'var(--surface)', border:`1px solid ${pct>=1?'rgba(192,57,43,.3)':pct>=0.8?'rgba(217,119,6,.25)':'var(--border)'}`, borderRadius:'var(--r)', padding:'12px 14px', marginBottom:8, boxShadow:'var(--sh)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8, gap:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>{item.name}</div>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--slate)', marginTop:2 }}>{item.system}</div>
          {item.notes && <div style={{ fontSize:11, color:'var(--slate)', marginTop:3, fontStyle:'italic' }}>{item.notes}</div>}
        </div>
        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)' }}>{item.intervalHrs?`Every ${item.intervalHrs}h`:`Every ${item.intervalDays}d`}</div>
          {item.intervalHrs&&item.lastHrs&&<div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:1 }}>Last {Number(item.lastHrs).toLocaleString()}h</div>}
          {item.lastDate&&<div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:1 }}>Last {item.lastDate}</div>}
        </div>
      </div>
      <div style={{ height:6, background:'var(--s2)', borderRadius:3, overflow:'hidden', marginBottom:5 }}>
        <div style={{ height:'100%', width:`${pct*100}%`, background:color, borderRadius:3 }}/>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, color }}>{status}</div>
        <button onClick={openEdit} style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', padding:'4px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--s2)', color:'var(--slate)', cursor:'pointer' }}>Log service</button>
      </div>
      {editing && (
        <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid var(--border)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.1em' }}>Service Date</div>
              <input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)}/>
            </div>
            {item.intervalHrs && (
              <div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.1em' }}>Hrs at service</div>
                <input type="number" step=".1" value={editHrs} onChange={e=>setEditHrs(e.target.value)} placeholder={String(item.system==='Generator'?genHrs:engHrs)} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/>
              </div>
            )}
            <div>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.1em' }}>Cost ($)</div>
              <input type="number" step=".01" value={editCost} onChange={e=>setEditCost(e.target.value)} placeholder="0.00" style={{ fontFamily:"'IBM Plex Mono',monospace" }}/>
            </div>
            <div>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.1em' }}>Vendor</div>
              <input type="text" value={editVendor} onChange={e=>setEditVendor(e.target.value)} placeholder="Fisheries Supply…"/>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:'var(--slate)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.1em' }}>Notes</div>
              <input type="text" value={editNote} onChange={e=>setEditNote(e.target.value)} placeholder="Part #, observations…"/>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setEditing(false)} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:600, padding:'7px 14px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--slate)', cursor:'pointer', flex:1 }}>Cancel</button>
            <button onClick={saveEdit} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:700, padding:'7px 14px', borderRadius:7, border:'none', background:'var(--navy)', color:'var(--gold-lt)', cursor:'pointer', flex:2 }}>Save service record</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Fuel Log ──────────────────────────────────────────────────
function FuelModal({ isOpen, onClose, onSave, currentEngHrs, currentGenHrs, lastFuelEntry, editEntry }) {
  const [form, setForm]   = useState({});
  const [unit, setUnit]   = useState('gal');
  const [priceSource, setPriceSource] = useState('ppg');
  const [saving, setSaving] = useState(false);
  const mobile = useIsMobile();

  React.useEffect(() => {
    if (!isOpen) return;
    if (editEntry) {
      setForm({
        ...editEntry,
        total_input: editEntry.total_gal != null ? editEntry.total_gal : '',
      });
      setUnit('gal');
      setPriceSource(editEntry.total_cost && !editEntry.price_per_gal ? 'total' : 'ppg');
    } else {
      setForm({
        date: new Date().toISOString().slice(0,10),
        engine_hrs: currentEngHrs || '',
        gen_hrs: currentGenHrs || '',
      });
      setUnit('gal');
      setPriceSource('ppg');
    }
  }, [isOpen, editEntry, currentEngHrs, currentGenHrs]);

  const set = (k,v) => setForm(f => ({ ...f, [k]:v }));

  const toGal    = (x) => unit==='gal' ? Number(x||0) : Number(x||0)/L_PER_GAL;
  const totalGal = toGal(form.total_input);
  const totalL   = totalGal * L_PER_GAL;
  const capMain  = unit==='gal' ? MAIN_CAP_GAL : Math.round(MAIN_CAP_GAL*L_PER_GAL);
  const capAux   = unit==='gal' ? AUX_CAP_GAL  : Math.round(AUX_CAP_GAL*L_PER_GAL);

  // Quantity (totalGal) is the single "total added" field. Whichever of price/gal
  // or total cost you type drives the other; the untouched field shows the derived value.
  const ppg = priceSource==='total'
    ? (totalGal>0 && form.total_cost ? Number(form.total_cost)/totalGal : 0)
    : Number(form.price_per_gal||0);
  const totalCost = priceSource==='total'
    ? (form.total_cost ? Number(form.total_cost) : null)
    : (ppg>0 ? ppg*totalGal : null);
  const ppgDisplay   = priceSource==='ppg'   ? (form.price_per_gal||'') : (ppg>0 ? ppg.toFixed(3) : '');
  const totalDisplay = priceSource==='total' ? (form.total_cost||'')    : (totalCost!=null ? totalCost.toFixed(2) : '');

  // Consumption estimate
  const hrsSinceLast = lastFuelEntry?.engine_hrs && form.engine_hrs
    ? Number(form.engine_hrs) - Number(lastFuelEntry.engine_hrs)
    : null;
  const galPerHr = hrsSinceLast && totalGal
    ? (totalGal / hrsSinceLast).toFixed(2)
    : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        main_gal: null,
        aux_gal:  null,
        total_gal: totalGal || null,
        main_level: form.main_level === '' ? null : form.main_level,
        aux_level:  form.aux_level  === '' ? null : form.aux_level,
        price_per_gal: ppg>0 ? Number(ppg.toFixed(3)) : null,
        total_cost: totalCost,
        gal_per_hr: galPerHr ? Number(galPerHr) : null,
        hrs_since_last: hrsSinceLast,
      });
      onClose();
    } catch {}
    finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:300, display:'flex', alignItems: mobile?'flex-end':'center', justifyContent:'center', padding: mobile?0:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'var(--surface)', borderRadius: mobile?'16px 16px 0 0':16, width:'100%', maxWidth:600, maxHeight: mobile?'88vh':'90vh', overflowY:'auto', boxShadow:'var(--sh-lg)' }}>
        <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'var(--surface)', zIndex:1 }}>
          <div style={{ fontSize:17, fontWeight:700, color:'var(--navy)' }}>{editEntry ? 'Edit Fuel Fill' : 'Log Fuel Fill'}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, color:'var(--slate)', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10, marginBottom:12 }}>
            <div><Lbl>Date</Lbl><input type="date" value={form.date||''} onChange={e=>set('date',e.target.value)}/></div>
            <div><Lbl>Location</Lbl><input type="text" value={form.location||''} onChange={e=>set('location',e.target.value)} placeholder="EBM Fuel Dock"/></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10, marginBottom:14 }}>
            <div><Lbl sub="★ For consumption calc">Engine Hrs</Lbl><input type="number" step=".1" value={form.engine_hrs||''} onChange={e=>set('engine_hrs',e.target.value)} style={{ fontFamily:"'IBM Plex Mono',monospace", borderColor:'var(--gold)' }}/></div>
            <div><Lbl>Gen Hrs</Lbl><input type="number" step=".1" value={form.gen_hrs||''} onChange={e=>set('gen_hrs',e.target.value)} style={{ fontFamily:"'IBM Plex Mono',monospace" }}/></div>
          </div>

          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--gold)', paddingBottom:8, marginBottom:10, borderBottom:'1px solid var(--border)' }}>Fuel Added</div>

          {/* Unit toggle */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
            {['gal','L'].map(u => (
              <button key={u} onClick={()=>setUnit(u)} style={{ padding:'10px', borderRadius:7, border: unit===u?'none':'1px solid var(--border)', background: unit===u?'var(--navy)':'var(--surface)', color: unit===u?'var(--gold-lt)':'var(--slate)', fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'center' }}>{u==='gal'?'Gallons':'Litres'}</button>
            ))}
          </div>

          {/* Total added + price (enter either price field; the other calculates) */}
          <div style={{ display:'grid', gridTemplateColumns: mobile?'1fr':'repeat(3, 1fr)', gap:10, marginBottom:14 }}>
            <div><Lbl sub={`Total pumped (${unit})`}>Total Added</Lbl><input type="number" step=".1" value={form.total_input||''} onChange={e=>set('total_input',e.target.value)} placeholder="24.5" style={{ fontFamily:"'IBM Plex Mono',monospace", fontWeight:700, borderColor:'var(--gold)' }}/></div>
            <div><Lbl sub="type either — other fills in">Price / gal ($)</Lbl><input type="number" step=".01" value={ppgDisplay} onChange={e=>{ set('price_per_gal',e.target.value); setPriceSource('ppg'); }} placeholder="5.49" style={{ fontFamily:"'IBM Plex Mono',monospace", fontWeight:700, borderColor: priceSource==='ppg'&&form.price_per_gal?'var(--gold)':'var(--border)' }}/></div>
            <div><Lbl sub="or the receipt total">Total Cost ($)</Lbl><input type="number" step=".01" value={totalDisplay} onChange={e=>{ set('total_cost',e.target.value); setPriceSource('total'); }} placeholder="—" style={{ fontFamily:"'IBM Plex Mono',monospace", fontWeight:700, borderColor: priceSource==='total'&&form.total_cost?'var(--gold)':'var(--border)' }}/></div>
          </div>

          {/* Resulting tank levels — quarter steps, independent of gallons added */}
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.14em', color:'var(--gold)', paddingBottom:8, marginBottom:10, borderBottom:'1px solid var(--border)' }}>Tank Levels After Fill</div>
          <div style={{ display:'grid', gridTemplateColumns: mobile?'1fr':'1fr 1fr', gap:10, marginBottom:12 }}>
            <LevelPicker label="Main Tank" cap={capMain} unit={unit} value={form.main_level} onChange={v=>set('main_level',v)}/>
            <LevelPicker label="Aux Tank"  cap={capAux}  unit={unit} value={form.aux_level}  onChange={v=>set('aux_level',v)}/>
          </div>

          {/* Totals */}
          {totalGal > 0 && (
            <div style={{ background:'var(--s2)', borderRadius:8, padding:'10px 12px', marginBottom:12, display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(80px, 1fr))', gap:10 }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:18, fontWeight:700, color:'var(--navy)' }}>{totalGal.toFixed(1)}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--slate)', marginTop:2 }}>Total gal</div>
              </div>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:18, fontWeight:700, color:'var(--navy)' }}>{totalL.toFixed(1)}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--slate)', marginTop:2 }}>Total L</div>
              </div>
              {hrsSinceLast > 0 && (
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:18, fontWeight:700, color:'var(--navy)' }}>+{hrsSinceLast.toFixed(1)}h</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--slate)', marginTop:2 }}>Since last</div>
                </div>
              )}
            </div>
          )}

          {/* Consumption estimate */}
          {galPerHr && (
            <div style={{ background:'rgba(184,134,11,.08)', border:'1px solid rgba(184,134,11,.25)', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--gold)', marginBottom:6 }}>Consumption estimate</div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
                <span style={{ fontSize:12, color:'var(--ink-s)' }}>{totalGal.toFixed(1)} gal ÷ {hrsSinceLast.toFixed(1)}h</span>
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:15, fontWeight:700, color:'var(--navy)' }}>{galPerHr} gal/hr</span>
              </div>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)' }}>
                {Number(galPerHr) <= 0.65 ? '≈ 1,800-2,000 RPM idle/slow' :
                 Number(galPerHr) <= 1.05 ? '≈ 2,200-2,400 RPM eco cruise' :
                 Number(galPerHr) <= 1.60 ? '≈ 2,600-2,800 RPM cruise' :
                 Number(galPerHr) <= 2.35 ? '≈ 3,000-3,200 RPM fast cruise' :
                 Number(galPerHr) <= 3.25 ? '≈ 3,400-3,600 RPM hard push' :
                 '≈ 3,800 RPM WOT'} · Yanmar 4JH3TE
              </div>
            </div>
          )}

          <div><Lbl>Notes</Lbl><input type="text" value={form.notes||''} onChange={e=>set('notes',e.target.value)} placeholder="Card ending XXXX, slip D-4…"/></div>
        </div>
        <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8, position:'sticky', bottom:0, background:'var(--surface)', paddingBottom:'max(12px, env(safe-area-inset-bottom))' }}>
          <button onClick={onClose} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, padding:'9px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--slate)', cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:700, padding:'9px 22px', borderRadius:7, border:'none', background:'linear-gradient(135deg,var(--navy),#1d3e63)', color:'var(--gold-lt)', opacity:saving?.7:1, cursor:'pointer' }}>
            {saving?'Saving…':editEntry?'Update':'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FuelCard({ entry, derived, onEdit, onDelete }) {
  const gph = derived?.gal_per_hr ?? null;
  const hsl = derived?.hrs_since_last ?? null;
  const lvl = (v) => v==null||v===''?null:(LEVELS.find(([n])=>Number(n)===Number(v))||[,'?'])[1];
  const mainL = lvl(entry.main_level), auxL = lvl(entry.aux_level);
  const readouts = [
    ['Gallons', entry.total_gal!=null ? entry.total_gal.toFixed(1) : '—', 'var(--navy)'],
    ['Cost',    entry.total_cost!=null ? `$${entry.total_cost.toFixed(2)}` : '—', 'var(--navy)'],
    ['gal / hr', gph!=null ? gph : '—', 'var(--gold)'],
    ['Since last', hsl!=null ? `+${Number(hsl).toFixed(1)}h` : '—', 'var(--navy)'],
  ];
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, boxShadow:'var(--sh)', padding:'15px 18px', marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--gold-deep, #8B6508)' }}>
          Fuel fill{entry.date ? ` · ${entry.date}` : ''}{entry.location ? ` · ${entry.location}` : ''}
        </span>
        <span style={{ flexShrink:0, marginLeft:10 }}>
          <button onClick={()=>onEdit(entry)} style={{ background:'none', border:'none', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--gold)', textDecoration:'underline', cursor:'pointer' }}>Edit</button>
          <button onClick={()=>onDelete(entry)} style={{ background:'none', border:'none', marginLeft:12, fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--red)', cursor:'pointer' }}>Delete</button>
        </span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10 }}>
        {readouts.map(([l,v,c]) => (
          <div key={l}>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:800, color:c, lineHeight:1 }}>{v}</div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)', marginTop:5 }}>{l}</div>
          </div>
        ))}
      </div>
      {(entry.price_per_gal || entry.engine_hrs || mainL || auxL) && (
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)', marginTop:13, paddingTop:11, borderTop:'1px solid var(--s2)' }}>
          {[
            entry.price_per_gal ? `$${entry.price_per_gal}/gal` : null,
            entry.engine_hrs ? `engine ${Number(entry.engine_hrs).toLocaleString()} h` : null,
            mainL ? `main ${mainL}` : null,
            auxL ? `aux ${auxL}` : null,
          ].filter(Boolean).join('  ·  ')}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
const SUB_TABS = ['Checks', 'Schedule', 'Fuel'];

export default function MaintenanceTab({ checks, serviceRecords=[], fuelLog=[], currentEngHrs=2940, currentGenHrs=1130, openAction=null, onActionHandled, onSaveCheck, onDeleteCheck, onSaveServiceRecord, onSaveFuel, onDeleteFuel }) {
  const [sub, setSub]             = useState('Checks');
  const [showCheck, setShowCheck] = useState(false);
  const [showFuel, setShowFuel]   = useState(false);
  const [editFuel, setEditFuel]   = useState(null);
  const [editCheck, setEditCheck] = useState(null);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [sysFilter, setSysFilter] = useState('All');
  const mobile = useIsMobile();

  // The + sheet can ask us to open a form directly.
  React.useEffect(() => {
    if (!openAction) return;
    if (openAction === 'fuel') { setSub('Fuel'); setEditFuel(null); setShowFuel(true); }
    if (openAction === 'check') { setSub('Checks'); setEditCheck(null); setShowCheck(true); }
    if (onActionHandled) onActionHandled();
  }, [openAction, onActionHandled]);

  const checksByDate = [...checks].sort((a,b) => new Date(b.date) - new Date(a.date));

  const schedule = DEFAULT_SCHEDULE.map(item => {
    const records = serviceRecords.filter(r=>r.item_id===item.id).sort((a,b)=>new Date(b.service_date||b.created_at)-new Date(a.service_date||a.created_at));
    const latest = records[0];
    if (!latest) return item;
    return { ...item, lastHrs:latest.service_hrs??item.lastHrs, lastDate:latest.service_date??item.lastDate, notes:latest.notes??item.notes };
  });

  const handleUpdate = async (id, updates) => {
    const item = DEFAULT_SCHEDULE.find(i=>i.id===id);
    try {
      await onSaveServiceRecord({ item_id:id, item_name:item?.name, system:item?.system, service_hrs:updates.lastHrs||null, service_date:updates.lastDate||new Date().toISOString().slice(0,10), cost:updates.cost||null, vendor:updates.vendor||null, notes:updates.notes||null });
    } catch(e) { console.error(e); }
  };

  const handleSaveCheck = async (raw) => {
    const { custom, heater, ...rest } = raw;
    await onSaveCheck({ ...rest, heater });
    if (custom?.name?.trim()) {
      await onSaveServiceRecord({ item_id:'custom_'+Date.now(), item_name:custom.name, system:'Custom', service_date:raw.date, service_hrs:custom.hrs?Number(custom.hrs):null, cost:custom.cost?Number(custom.cost):null, vendor:custom.vendor||null, notes:custom.notes||null, performed_by:raw.performed_by });
    }
  };

  const overdue = schedule.filter(i => {
    if (i.intervalHrs) { const hrs=i.system==='Generator'?currentGenHrs:currentEngHrs; return (hrs-i.lastHrs)>=i.intervalHrs; }
    return (new Date()-new Date(i.lastDate))/86400000>=i.intervalDays;
  }).length;

  // Fuel consumption is derived oldest→newest: each fill vs the fill immediately before it.
  const fuelAsc = [...fuelLog].sort((a,b)=> (new Date(a.date)-new Date(b.date)) || ((Number(a.engine_hrs)||0)-(Number(b.engine_hrs)||0)));
  const fuelDerived = {};
  for (let i=0;i<fuelAsc.length;i++) {
    const cur=fuelAsc[i], prev=fuelAsc[i-1];
    let hsl=null, gph=null;
    if (prev && cur.engine_hrs!=null && prev.engine_hrs!=null) {
      const d = Number(cur.engine_hrs) - Number(prev.engine_hrs);
      if (d>0) { hsl=d; if (Number(cur.total_gal)>0) gph=Number((Number(cur.total_gal)/d).toFixed(2)); }
    }
    fuelDerived[cur.id] = { hrs_since_last:hsl, gal_per_hr:gph };
  }
  const fuelDesc = [...fuelAsc].reverse();     // newest at top for display
  const lastFuel = fuelDesc[0];
  const gphVals = Object.values(fuelDerived).map(d=>d.gal_per_hr).filter(v=>v!=null);
  const avgGph = gphVals.length ? (gphVals.reduce((s,v)=>s+v,0)/gphVals.length).toFixed(2) : null;
  const totalFuelGal = fuelLog.reduce((s,e)=>s+(Number(e.total_gal)||0),0);
  const totalFuelCost = fuelLog.reduce((s,e)=>s+(Number(e.total_cost)||0),0);

  // ── Status since the last fuel-up ──
  // At the last fill the tank was topped to capacity. Consumption since then is
  // estimated from engine hours run × the cruise burn (2400 rpm). Range applies
  // a 10% safety margin so you never plan to the last drop.
  const CRUISE = FUEL_RANGE_CURVE.find(p => p.rpm === 2400) || { rpm:2400, gph:1.04, kts:6.3 };
  const tankCap = MAIN_CAP_GAL + AUX_CAP_GAL;
  const lastFillEngHrs = lastFuel ? Number(lastFuel.engine_hrs) : null;
  const engNow = Number(currentEngHrs) || null;
  const hrsSinceFill = (lastFillEngHrs != null && engNow != null && engNow >= lastFillEngHrs)
    ? +(engNow - lastFillEngHrs).toFixed(1) : null;
  const usedSinceFill = hrsSinceFill != null ? +(hrsSinceFill * CRUISE.gph).toFixed(1) : null;
  const remainingFuel = usedSinceFill != null ? Math.max(+(tankCap - usedSinceFill).toFixed(1), 0) : null;
  const usableFuel = remainingFuel != null ? Math.max(remainingFuel * 0.9, 0) : null; // 10% margin
  const rangeNm = usableFuel != null ? Math.round((usableFuel / CRUISE.gph) * CRUISE.kts) : null;
  const rangeHrs = usableFuel != null ? +(usableFuel / CRUISE.gph).toFixed(1) : null;

  const tabStyle = t => ({
    fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase',
    padding:'10px 16px', border:'none', background:'none',
    color: sub===t?'var(--gold)':'var(--slate)',
    borderBottom: sub===t?'2px solid var(--gold)':'2px solid transparent',
    marginBottom:-1, cursor:'pointer', transition:'color .18s, border-color .18s',
    position:'relative',
  });

  const systems = ['All','Engine','Generator','Heater','Seasonal'];
  const filtered = schedule.filter(i=>sysFilter==='All'||i.system===sysFilter);

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:20 }}>
        {SUB_TABS.map(t => (
          <button key={t} style={tabStyle(t)} onClick={()=>setSub(t)}>
            {t}
            {t==='Schedule'&&overdue>0&&<span style={{ marginLeft:5, background:'var(--red)', color:'#fff', borderRadius:999, fontSize:9, fontWeight:700, padding:'1px 5px' }}>{overdue}</span>}
          </button>
        ))}
      </div>

      {/* ── CHECKS ── */}
      {sub==='Checks' && (
        <>
          {mobile && selectedCheck && checksByDate.find(c=>c.id===selectedCheck) && (
            <div style={{ position:'fixed', inset:0, zIndex:250, background:'var(--bg)', overflowY:'auto' }}>
              <CheckDetailView entry={checksByDate.find(c=>c.id===selectedCheck)} onEdit={ent=>{setSelectedCheck(null);setEditCheck(ent);setShowCheck(true);}} onDelete={ent=>{onDeleteCheck(ent);setSelectedCheck(null);}} onBack={()=>setSelectedCheck(null)}/>
            </div>
          )}
          {checks.length===0
            ? <div style={{ textAlign:'center', padding:'48px 20px', color:'var(--slate)', fontSize:14 }}>No checks yet. Tap + and choose Maintenance to record oil, belt, and strainer status.</div>
            : mobile
              ? checksByDate.map(e=><CheckSummaryCard key={e.id} entry={e} onOpen={ent=>setSelectedCheck(ent.id)}/>)
              : (
                <div style={{ display:'grid', gridTemplateColumns:'360px 1fr', gap:16, alignItems:'start', marginBottom:8 }}>
                  <div style={{ maxHeight:'calc(100dvh - 300px)', overflowY:'auto', paddingRight:4 }}>
                    {checksByDate.map(e=><CheckSummaryCard key={e.id} entry={e} active={(checksByDate.find(c=>c.id===selectedCheck)||checksByDate[0])?.id===e.id} onOpen={ent=>setSelectedCheck(ent.id)}/>)}
                  </div>
                  <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'var(--sh)', minHeight:360 }}>
                    <CheckDetailView entry={checksByDate.find(c=>c.id===selectedCheck)||checksByDate[0]} onEdit={ent=>{setEditCheck(ent);setShowCheck(true);}} onDelete={onDeleteCheck} onBack={null}/>
                  </div>
                </div>
              )
          }
          {serviceRecords.filter(r=>r.system==='Custom').sort((a,b)=>new Date(b.service_date||b.created_at)-new Date(a.service_date||a.created_at)).map(r => (
            <div key={r.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', marginBottom:10, overflow:'hidden', boxShadow:'var(--sh)' }}>
              <div style={{ padding:'11px 16px', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--slate)', marginBottom:2 }}>{r.service_date} · Custom</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'var(--navy)' }}>{r.item_name}</div>
                  {r.vendor && <div style={{ fontSize:12, color:'var(--slate)', marginTop:2 }}>{r.vendor}</div>}
                  {r.notes  && <div style={{ fontSize:12, color:'var(--ink-s)', marginTop:2, fontStyle:'italic' }}>{r.notes}</div>}
                </div>
                <div style={{ textAlign:'right' }}>
                  {r.cost && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:700, color:'var(--navy)' }}>${Number(r.cost).toFixed(2)}</div>}
                  {r.service_hrs && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:2 }}>{r.service_hrs}h</div>}
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── SCHEDULE ── */}
      {sub==='Schedule' && (
        <>
          <div style={{ background:'linear-gradient(135deg,var(--navy),#16304d)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:14, display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[['Engine Hrs', Number(currentEngHrs).toLocaleString()+'h'],['Generator Hrs', Number(currentGenHrs).toLocaleString()+'h']].map(([l,v])=>(
              <div key={l} style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:22, fontWeight:800, color:'var(--gold-lt)' }}>{v}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,.45)', marginTop:3 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:0, flexWrap:'wrap', marginBottom:14, borderBottom:'1px solid var(--border)' }}>
            {systems.map(s=>(
              <button key={s} onClick={()=>setSysFilter(s)} style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', padding:'5px 0', marginRight:18, background:'none', border:'none', cursor:'pointer', color:sysFilter===s?'var(--navy)':'var(--slate)', borderBottom:sysFilter===s?'2px solid var(--gold)':'2px solid transparent' }}>{s}</button>
            ))}
          </div>
          {filtered.map(item=><ScheduleRow key={item.id} item={item} engHrs={currentEngHrs} genHrs={currentGenHrs} onUpdate={handleUpdate}/>)}
          <div style={{ marginTop:12, padding:'10px 14px', background:'var(--s2)', borderRadius:8, fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)' }}>
            Update engine/generator hours in Settings (⚙ on Boat tab) to keep the schedule accurate.
          </div>
        </>
      )}

      {/* ── FUEL ── */}
      {sub==='Fuel' && (
        <>
          {lastFuel && (
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'16px 16px 14px', marginBottom:14, boxShadow:'var(--sh)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:12 }}>
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--gold)' }}>Since Last Fuel-Up</span>
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)' }}>{lastFuel.date}{lastFuel.location ? ` · ${lastFuel.location}` : ''}</span>
              </div>
              {remainingFuel != null ? (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px 18px' }}>
                    {[
                      ['Engine hours run', hrsSinceFill != null ? `${hrsSinceFill} h` : '—', 'since fill'],
                      ['Est. consumption', usedSinceFill != null ? `${usedSinceFill} gal` : '—', `at ${CRUISE.gph} gph`],
                      ['Remaining fuel', remainingFuel != null ? `${remainingFuel} gal` : '—', `of ${tankCap} gal`],
                      ['Range at 2400 rpm', rangeNm != null ? `${rangeNm} nm` : '—', `${rangeHrs}h · 10% margin`],
                    ].map(([l,v,sub2],i)=>(
                      <div key={l} style={ i%2 ? { paddingLeft:18, borderLeft:'1px solid var(--hair, #E7E1D5)' } : undefined }>
                        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:700, color: (l==='Remaining fuel' && remainingFuel < tankCap*0.2) ? 'var(--red)' : 'var(--navy)', lineHeight:1 }}>{v}</div>
                        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, fontWeight:600, color:'var(--slate)', marginTop:4 }}>{l}</div>
                        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9.5, color:'var(--slate)', marginTop:1 }}>{sub2}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:10.5, color:'var(--slate)', marginTop:12, lineHeight:1.5 }}>
                    Estimated from engine hours ({engNow} now) × cruise burn at 2400 rpm ({CRUISE.kts} kts). Fill the tank and log the fill to reset.
                  </div>
                </>
              ) : (
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12.5, color:'var(--slate)', lineHeight:1.5 }}>
                  {lastFillEngHrs == null
                    ? 'Add the engine hours at the last fill to see consumption and range. Edit that fuel entry and fill in "Engine hrs at fill."'
                    : `Current engine reading (${engNow}h) is below the last fill (${lastFillEngHrs}h) — check the numbers.`}
                </div>
              )}
            </div>
          )}
          {fuelLog.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:14 }}>
              {[
                ['Last fill', lastFuel?.total_gal ? `${Number(lastFuel.total_gal).toFixed(1)} gal` : '—'],
                ['Avg gal/hr', avgGph || '—'],
                ['YTD cost', totalFuelCost ? `$${totalFuelCost.toFixed(0)}` : '—'],
              ].map(([l,v])=>(
                <div key={l} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'10px 8px', textAlign:'center', boxShadow:'var(--sh)' }}>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:16, fontWeight:700, color:'var(--navy)', lineHeight:1, marginBottom:3 }}>{v}</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, fontWeight:600, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--slate)' }}>{l}</div>
                </div>
              ))}
            </div>
          )}
          {fuelLog.length===0
            ? <div style={{ textAlign:'center', padding:'48px 20px', color:'var(--slate)', fontSize:14 }}>No fuel fills logged yet. Tap + and choose Fuel entry.</div>
            : fuelDesc.map(e=><FuelCard key={e.id} entry={e} derived={fuelDerived[e.id]} onEdit={ent=>{setEditFuel(ent);setShowFuel(true);}} onDelete={onDeleteFuel}/>)
          }
        </>
      )}

      <CheckModal isOpen={showCheck} onClose={()=>{setShowCheck(false);setEditCheck(null);}} onSave={handleSaveCheck} currentEngHrs={currentEngHrs} currentGenHrs={currentGenHrs} editEntry={editCheck}/>
      <FuelModal isOpen={showFuel} onClose={()=>{setShowFuel(false);setEditFuel(null);}} onSave={onSaveFuel} currentEngHrs={currentEngHrs} currentGenHrs={currentGenHrs} lastFuelEntry={lastFuel} editEntry={editFuel}/>
    </div>
  );
}
